import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  fmt, groundData, getFillPercent, RACEWAY_TYPES,
  nextStdService, findServiceConductor, GEC_TABLE, STD_SERVICE_SIZES,
} from "./electrical-helpers";
import { readSettings } from "@/hooks/use-settings";
import type { useJobLog } from "@/hooks/use-job-log";

type SaveFn = (entry: Parameters<ReturnType<typeof useJobLog>["addEntry"]>[0]) => void;
const defaults = readSettings();

const SERVICE_WIRE_AREAS: Record<string, number> = {
  "14 AWG": 0.0097, "12 AWG": 0.0133, "10 AWG": 0.0211, "8 AWG": 0.0366,
  "6 AWG": 0.0507, "4 AWG": 0.0824, "3 AWG": 0.0973, "2 AWG": 0.1158,
  "1 AWG": 0.1562, "1/0": 0.1855, "2/0": 0.2223, "3/0": 0.2679, "4/0": 0.3237,
  "250 kcmil": 0.3970, "300 kcmil": 0.4608, "350 kcmil": 0.5242,
  "400 kcmil": 0.5917, "500 kcmil": 0.7073, "600 kcmil": 0.8676,
  "750 kcmil": 1.0496, "1000 kcmil": 1.3478,
};

const getParallelCount = (sizeLabel: string): number => {
  const match = sizeLabel.match(/^(\d+)×/);
  return match ? parseInt(match[1]) : 1;
};

export const ServiceEntranceSizing = ({ initialDemandVA, onConsumedInitial, cameFrom, onGoBack, onSave }: { initialDemandVA?: number | null; onConsumedInitial?: () => void; cameFrom?: string | null; onGoBack?: (tool: string) => void; onSave?: SaveFn }) => {
  const [demandVA, setDemandVA] = useState(initialDemandVA ? String(initialDemandVA) : "");
  const [voltage, setVoltage] = useState(defaults.voltage);
  const [phases, setPhases] = useState<string>(defaults.phases);
  const [conductorMaterial, setConductorMaterial] = useState<"cu" | "al">(defaults.conductorMaterial);
  const [racewayType, setRacewayType] = useState<string>(defaults.racewayType);

  useEffect(() => {
    if (initialDemandVA && initialDemandVA > 0) {
      setDemandVA(String(initialDemandVA));
      onConsumedInitial?.();
    }
  }, [initialDemandVA]);

  const demand = parseFloat(demandVA) || 0;
  const V = parseFloat(voltage);
  const ph = parseInt(phases);
  const phaseDivisor = ph === 3 ? 1.732 : 1;
  const demandAmps = demand > 0 ? demand / (V * phaseDivisor) : 0;
  const serviceSize = demandAmps > 0 ? nextStdService(demandAmps) : 0;
  const conductor = demandAmps > 0 ? findServiceConductor(serviceSize) : null;
  const egc = serviceSize > 0 ? groundData.find(r => r.ocpd >= serviceSize) : null;

  const gecIdx = conductor
    ? GEC_TABLE.findIndex(g =>
        conductorMaterial === "cu"
          ? g.maxServiceCu.includes(conductor.cuSize.replace(" AWG", ""))
          : g.maxServiceAl.includes(conductor.alSize.replace(" AWG", ""))
      )
    : -1;
  const gec = gecIdx >= 0 ? GEC_TABLE[gecIdx] : GEC_TABLE[GEC_TABLE.length - 1];

  const conductorSize = conductor ? (conductorMaterial === "cu" ? conductor.cuSize : conductor.alSize) : null;
  const egcSize = egc ? (conductorMaterial === "cu" ? egc.copper : egc.aluminum) : null;
  const parallelSets = conductorSize ? getParallelCount(conductorSize) : 1;

  const phaseCount = ph === 3 ? 3 : 2;
  const neutralCount = 1;
  const egcCount = 1;
  const totalWiresPerSet = phaseCount + neutralCount + egcCount;

  const baseConductorSize = conductorSize?.replace(/^\d+×\s*/, "") ?? null;
  const phaseArea = baseConductorSize ? (SERVICE_WIRE_AREAS[baseConductorSize] ?? 0) : 0;
  const egcArea = egcSize ? (SERVICE_WIRE_AREAS[egcSize] ?? 0) : 0;
  const totalFillPerSet = (phaseCount * phaseArea) + (neutralCount * phaseArea) + (egcCount * egcArea);
  const fillPct = getFillPercent(totalWiresPerSet);

  const activeRaceway = RACEWAY_TYPES.find(r => r.value === racewayType) ?? RACEWAY_TYPES[0];
  const recommendedConduit = totalFillPerSet > 0
    ? activeRaceway.sizes.find(c => c.area * fillPct >= totalFillPerSet) ?? null
    : null;

  const hasInput = demand > 0 && V > 0;

  return (
    <div className="space-y-3">
      {cameFrom && onGoBack && (
        <button
          onClick={() => onGoBack(cameFrom)}
          className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-semibold transition-colors -mb-1"
        >
          <span>←</span>
          <span>Back to {cameFrom === "panel" ? "Panel Schedule" : cameFrom === "demand" ? "Demand Factor" : cameFrom}</span>
        </button>
      )}
      <p className="text-[11px] text-muted-foreground leading-relaxed">Size service entrance equipment — main breaker, service conductors, EGC, and GEC per NEC Articles 230, 250, and 310.</p>

      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Demand Load (VA)</label><Input type="number" inputMode="numeric" value={demandVA} onChange={e => setDemandVA(e.target.value)} placeholder="e.g. 24000" className="bg-secondary border-border font-mono-data text-lg" /></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Voltage</label><Select value={voltage} onValueChange={setVoltage}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent>{["120","208","240","480"].map(v => <SelectItem key={v} value={v}>{v}V</SelectItem>)}</SelectContent></Select></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Phases</label><Select value={phases} onValueChange={setPhases}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="1">1Φ</SelectItem><SelectItem value="3">3Φ</SelectItem></SelectContent></Select></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Conductor</label><Select value={conductorMaterial} onValueChange={(v: "cu" | "al") => setConductorMaterial(v)}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cu">Copper</SelectItem><SelectItem value="al">Aluminum</SelectItem></SelectContent></Select></div>
      </div>

      {hasInput && serviceSize > 0 && (
        <div className="space-y-2 animate-fade-in">
          <div className="trade-card border-primary/40 bg-primary/5">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Main Breaker / Service</span>
              <span className="font-mono-data text-xl text-primary font-bold">{serviceSize}A</span>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">{Math.round(demand).toLocaleString()} VA ÷ ({V}V{ph === 3 ? " × √3" : ""}) = {fmt(demandAmps, 1)}A</p>
          </div>

          {conductor && (
            <div className="trade-card border-accent/40 bg-accent/5">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Service Conductors — NEC 310.12</h3>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <span className="text-muted-foreground">Copper:</span>
                <span className={`font-mono-data font-bold text-right ${conductorMaterial === "cu" ? "text-primary" : "text-foreground"}`}>{conductor.cuSize}</span>
                <span className="text-muted-foreground">Aluminum:</span>
                <span className={`font-mono-data font-bold text-right ${conductorMaterial === "al" ? "text-primary" : "text-foreground"}`}>{conductor.alSize}</span>
              </div>
            </div>
          )}

          {egc && (
            <div className="trade-card border-safety/40 bg-safety/5">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Equipment Grounding — NEC 250.122</h3>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <span className="text-muted-foreground">EGC (Copper):</span><span className="font-mono-data font-bold text-right">{egc.copper}</span>
                <span className="text-muted-foreground">EGC (Aluminum):</span><span className="font-mono-data font-bold text-right">{egc.aluminum}</span>
              </div>
            </div>
          )}

          {gec && (
            <div className="trade-card border-conduit/40 bg-conduit/5">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Grounding Electrode Conductor — NEC 250.66</h3>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <span className="text-muted-foreground">GEC (Copper):</span><span className="font-mono-data font-bold text-right">{gec.gecCu}</span>
                <span className="text-muted-foreground">GEC (Aluminum):</span><span className="font-mono-data font-bold text-right">{gec.gecAl}</span>
              </div>
            </div>
          )}

          {totalFillPerSet > 0 && (
            <div className="trade-card border-copper/40 bg-copper/5">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Conduit Fill — NEC Ch. 9 Table 1{parallelSets > 1 ? ` (per set of ${parallelSets})` : ""}</h3>
              <div className="flex gap-1 mb-3 bg-secondary/50 rounded-lg p-1">
                {RACEWAY_TYPES.map(rt => (
                  <button key={rt.value} onClick={() => setRacewayType(rt.value)} className={`flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-all ${racewayType === rt.value ? "bg-card text-foreground shadow-sm border border-border" : "text-muted-foreground hover:text-foreground"}`}>{rt.label}</button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <span className="text-muted-foreground">Conductors:</span>
                <span className="font-mono-data font-bold text-right">
                  {phaseCount}× {baseConductorSize} + 1N + 1 EGC ({egcSize})
                </span>
                <span className="text-muted-foreground">Total wires:</span>
                <span className="font-mono-data font-bold text-right">{totalWiresPerSet} ({Math.round(fillPct * 100)}% fill allowed)</span>
                <span className="text-muted-foreground">Total fill area:</span>
                <span className="font-mono-data font-bold text-right">{fmt(totalFillPerSet, 3)} in²</span>
                {recommendedConduit && <>
                  <span className="text-muted-foreground">Min conduit ({activeRaceway.label}):</span>
                  <span className="font-mono-data font-bold text-right text-primary">{recommendedConduit.label}</span>
                  <span className="text-muted-foreground">Conduit area:</span>
                  <span className="font-mono-data font-bold text-right">{fmt(recommendedConduit.area, 3)} in² ({fmt((totalFillPerSet / recommendedConduit.area) * 100, 1)}% used)</span>
                </>}
              </div>
              {!recommendedConduit && <p className="text-sm text-destructive font-semibold mt-2">⚠️ No standard {activeRaceway.label} size fits — consider parallel runs</p>}
              {parallelSets > 1 && recommendedConduit && (
                <p className="text-[10px] text-muted-foreground mt-2">⚡ {parallelSets} parallel sets required — each set needs {recommendedConduit.label} conduit</p>
              )}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {activeRaceway.sizes.map(c => {
                  const fits = totalFillPerSet <= c.area * fillPct;
                  const isSelected = recommendedConduit?.label === c.label;
                  return (
                    <span key={c.label} className={`px-2 py-1 rounded text-[10px] font-mono-data border ${isSelected ? "bg-primary/15 border-primary/40 text-primary font-bold" : !fits ? "bg-destructive/10 border-destructive/30 text-destructive line-through" : "bg-card border-border text-muted-foreground"}`}>
                      {c.label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          <div className="trade-card border-muted/40">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Service Summary</h3>
            <div className="space-y-1 text-xs font-mono-data">
              <div className="flex justify-between"><span className="text-muted-foreground">Demand Load</span><span className="text-foreground font-bold">{Math.round(demand).toLocaleString()} VA</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Demand Amps</span><span className="text-foreground font-bold">{fmt(demandAmps, 1)} A</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Main Breaker</span><span className="text-primary font-bold">{serviceSize} A</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Service Conductors ({conductorMaterial === "cu" ? "Cu" : "Al"})</span><span className="text-foreground font-bold">{conductor ? (conductorMaterial === "cu" ? conductor.cuSize : conductor.alSize) : "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Min conduit conductors</span><span className="text-foreground font-bold">{ph === 3 ? "3 phase + 1 neutral + 1 EGC" : "2 ungrounded + 1 neutral + 1 EGC"}</span></div>
            </div>
          </div>

          <div className="trade-card border-muted/40">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Standard Service Sizes</h3>
            <div className="flex flex-wrap gap-1.5">
              {STD_SERVICE_SIZES.filter(s => s <= 2000).map(size => (
                <span key={size} className={`px-2 py-1 rounded text-[10px] font-mono-data border ${size === serviceSize ? "bg-primary/15 border-primary/40 text-primary font-bold" : size < demandAmps ? "bg-destructive/10 border-destructive/30 text-destructive line-through" : "bg-card border-border text-muted-foreground"}`}>{size}A</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {hasInput && serviceSize > 0 && onSave && (
        <button type="button" onClick={() => {
          onSave({
            calcType: "service-entrance", label: `${serviceSize}A Service — ${Math.round(demand).toLocaleString()} VA @ ${V}V ${ph}Φ`,
            inputs: { demand: `${Math.round(demand).toLocaleString()} VA`, voltage: `${V}V`, phases: `${ph}Φ`, conductor: conductorMaterial === "cu" ? "Copper" : "Aluminum" },
            results: [
              { label: "Main Breaker", value: String(serviceSize), unit: "A" },
              { label: "Demand Amps", value: fmt(demandAmps, 1), unit: "A" },
              { label: "Service Conductor", value: conductor ? (conductorMaterial === "cu" ? conductor.cuSize : conductor.alSize) : "—", unit: conductorMaterial === "cu" ? "Cu" : "Al" },
              { label: "EGC", value: egc ? (conductorMaterial === "cu" ? egc.copper : egc.aluminum) : "—", unit: "" },
              ...(recommendedConduit ? [{ label: "Min Conduit", value: recommendedConduit.label, unit: activeRaceway.label }] : []),
            ],
            note: "",
          });
          toast.success("Saved to Job Log");
        }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
      )}

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">⚠️ Service entrance sizing per NEC Articles 230, 250, and 310. Use the Demand Factor calculator to determine demand load. Always verify with local AHJ.</p>
    </div>
  );
};
